import { Injectable } from '@angular/core';
import Dexie from "dexie";
import {StorageService} from "./storage.service";

/*
https://dexie.org/docs/API-Reference
 */

@Injectable({
  providedIn: 'root'
})
export class LocaldbService {

  private db;
  private dbName = 'forta_local_db';
  private dbVersion = 4;

  private table_config = 'table_config_';
  private table_icd_de = 'table_icd_de';
  private table_icd_en = 'table_icd_en';
  private table_atc_de = 'table_atc_de';
  private table_atc_en = 'table_atc_en';
  private table_sync = 'table_sync';

  constructor(
    private storageService: StorageService,
  ) {
    this.init().then();
  }

  async init() {
    this.db = new Dexie(this.dbName);

    await this.db.version(this.dbVersion).stores({
      table_config_en: "++local_id, disclaimer, about, apphints, credentials, dsgvo",
      table_config_de: "++local_id, disclaimer, about, apphints, credentials, dsgvo",
      table_atc_de: "++local_id, &atc_id, icd_id, atc_beschreibung, atc_code, atc_klasse, atc_synonym, atc_kommentar",
      table_atc_en: "++local_id, &atc_id, icd_id, atc_beschreibung, atc_code, atc_klasse, atc_synonym, atc_kommentar",
      table_icd_de: "++local_id, &icd_id, icd_beschreibung, icd_code, icd_synonym, icd_kommentar",
      table_icd_en: "++local_id, &icd_id, icd_beschreibung, icd_code, icd_synonym, icd_kommentar",
      table_sync: "++local_id, config_sync_time, atc_sync_time, icd_sync_time"
    });
    await this.db.open();
  }

  async getDefaultLanguage() {
    const lng = await this.storageService.language();
    if (lng === null) {
      await this.storageService.language('de');
      return 'de';
    } else {
      return lng;
    }
  }

  async getUserConfigs(fieldName) {

    const lng = await this.getDefaultLanguage();
    const table_name = (this.table_config + lng).toString();

    const result = await this.db.table(table_name).toArray();
    if (result.length > 0) {
      if (result[0][fieldName]) {
        return result[0][fieldName];
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  async indicationSearch(searchText) {
    const lng = await this.getDefaultLanguage();
    let table_atc = '';
    let table_icd = '';
    if (lng === 'en') {
      table_atc = this.table_atc_en;
      table_icd = this.table_icd_en;
    } else if (lng === 'de') {
      table_atc = this.table_atc_de;
      table_icd = this.table_icd_de;
    }

    if (table_icd !== '') {
      searchText = searchText.toLowerCase();

      const result = await this.db.table(table_icd).orderBy('icd_beschreibung').filter((row) => {
        return (row.icd_beschreibung.toLocaleLowerCase().includes(searchText) || row.icd_synonym.toLocaleLowerCase().includes(searchText));
      }).toArray();

      if (result.length > 0) {
        const returnArray = [];
        for (const item of result) {
          const res = await this.db.table(table_atc).where('icd_id').equals(item.icd_id).toArray();

          if (res.length > 0) {
            returnArray.push({
              icd_beschreibung: item.icd_beschreibung,
              icd_id: item.icd_id,
              icd_synonym: item.icd_synonym
            });
          }
        }
        return returnArray;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  async substanceSearch(searchText) {
    const lng = await this.getDefaultLanguage();
    let table_atc = '';
    let table_icd = '';
    if (lng === 'en') {
      table_atc = this.table_atc_en;
      table_icd = this.table_icd_en;
    } else if (lng === 'de') {
      table_atc = this.table_atc_de;
      table_icd = this.table_icd_de;
    }

    if (table_icd !== '') {
      searchText = searchText.toLowerCase();

      const result = await this.db.table(table_atc).orderBy('atc_beschreibung').filter((row) => {
        return (row.atc_synonym.toLocaleLowerCase().includes(searchText) || row.atc_beschreibung.toLocaleLowerCase().includes(searchText));
      }).toArray();

      if (result.length > 0) {
        const returnArray = [];
        for (const item of result) {
          const res = await this.db.table(table_icd).where('icd_id').equals(item.icd_id).toArray();

          if (res.length > 0) {
            returnArray.push({
              atc_id: item.atc_id,
              atc_beschreibung: item.atc_beschreibung,
              atc_klasse: item.atc_klasse,
              atc_kommentar: item.atc_kommentar,
              icd_beschreibung: res[0].icd_beschreibung,
              icd_id: res[0].icd_id
            });
          }
        }
        return returnArray;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  async atcCodeSearch(searchText) {
    const lng = await this.getDefaultLanguage();
    let table_atc = '';
    let table_icd = '';
    if (lng === 'en') {
      table_atc = this.table_atc_en;
      table_icd = this.table_icd_en;
    } else if (lng === 'de') {
      table_atc = this.table_atc_de;
      table_icd = this.table_icd_de;
    }

    if (table_atc !== '') {
      searchText = searchText.toLowerCase();

      const result = await this.db.table(table_atc).where('atc_code').startsWithIgnoreCase(searchText).toArray();
      if (result.length > 0) {
        const returnArray = [];
        for (const item of result) {
          const res = await this.db.table(table_icd).where('icd_id').equals(item.icd_id).toArray();

          if (res.length > 0) {
            returnArray.push({
              atc_code: item.atc_code,
              atc_beschreibung: item.atc_beschreibung,
              atc_id: item.atc_id,
              atc_klasse: item.atc_klasse,
              atc_kommentar: item.atc_kommentar,
              icd_beschreibung: res[0].icd_beschreibung,
              icd_id: res[0].icd_id
            });
          }
        }
        return returnArray;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  async icdCodeSearch(searchText) {
    const lng = await this.getDefaultLanguage();
    let table_atc = '';
    let table_icd = '';
    if (lng === 'en') {
      table_atc = this.table_atc_en;
      table_icd = this.table_icd_en;
    } else if (lng === 'de') {
      table_atc = this.table_atc_de;
      table_icd = this.table_icd_de;
    }

    if (table_icd !== '') {
      searchText = searchText.toLowerCase();

      const result = await this.db.table(table_icd).orderBy('icd_beschreibung').filter((row) => {
        return (row.icd_code.toLocaleLowerCase().includes(searchText));
      }).toArray();

      if (result.length > 0) {

        const returnArray = [];
        for (const item of result) {
          const res = await this.db.table(table_atc).where('icd_id').equals(item.icd_id).toArray();

          if (res.length > 0) {
            returnArray.push(item);
          }
        }
        return returnArray;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  async detailSearch(id) {
    const lng = await this.getDefaultLanguage();
    let table = '';
    if (lng === 'en') {
      table = this.table_atc_en;
    } else if (lng === 'de') {
      table = this.table_atc_de;
    }

    if (table !== '') {
      const result = await this.db.table(table).where({'icd_id': id}).sortBy('atc_klasse');
      if (result.length > 0) {
        return result;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  async setICDTable(data, lng) {
    if (data.length > 0) {

      let table = '';
      if (lng === 'de') {
        table = this.table_icd_de;
      } else if (lng === 'en') {
        table = this.table_icd_en;
      }

      if (table !== '') {
        await this.db.table(table).clear();

        for (const item of data) {
          await this.db.table(table).put(item);
        }

        await this.setSyncTable('icd_sync_time');
      }
    }
  }

  async setATCTable(data, lng) {
    if (data.length > 0) {
      let table = '';
      if (lng === 'de') {
        table = this.table_atc_de;
      } else if (lng === 'en') {
        table = this.table_atc_en;
      }

      if (table !== '') {
        await this.db.table(table).clear();

        for (const item of data) {
          await this.db.table(table).put(item);
        }

        await this.setSyncTable('atc_sync_time');
      }
    }
  }

  async setConfigTable(data, lng) {
    if (typeof data === "object") {
      let table = '';
      if (lng === 'de') {
        table = this.table_config + lng;
      } else if (lng === 'en') {
        table = this.table_config + lng;
      }

      if (table !== '') {
        await this.db.table(table).clear();

        await this.db.table(table).put({'disclaimer': data.disclaimer, 'about': data.about, 'apphints': data.apphints, 'credentials': data.credentials, 'dsgvo': data.dsgvo});

        await this.setSyncTable('config_sync_time');
      }
    }
  }

  async setSyncTable(fieldName) {
    const result = await this.db.table(this.table_sync).toArray();
    if (result === null || result.length < 1) {
      await this.db.table(this.table_sync).put({[fieldName]: new Date().getTime()});
    } else {
      const localID = result[0].local_id;
      await this.db.table(this.table_sync).where({local_id: localID}).modify({[fieldName]: new Date().getTime()});
    }
  }

  async getSyncData() {
    const result = await this.db.table(this.table_sync).limit(1).toArray();
    if (result.length > 0) {
      return result[0];
    } else {
      return null;
    }
  }
}
